/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { RootStore } from 'src/core/stores/RootStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { OperationRetryStrategies } from 'src/core/utils/api/operationRetryStrategies';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getAppVersionService } from 'src/luis/core/services/apps/appVersionService';
import { getCompositeEntityService } from 'src/luis/core/services/entities/compositeEntityService';
import { addGlobalFeaturesToNewModels } from 'src/luis/core/utils/addGlobalFeaturesToNewModels';
import { deleteDanglingFeaturePointer } from 'src/luis/modules/features/utils/deleteDanglingFeaturePointer';
import { trainAppVersion } from 'src/luis/modules/training/trainingHandlerGroup';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { CompositeEntitiesMigrationTrackerStore } from 'src/luis/types/stores/entities/CompositeEntitiesMigrationTrackerStore';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';

export type CompositeEntityHandlerGroup = ReturnType<typeof createCompositeEntityHandlerGroup>;

/*
 * Gets the names of the children of a CompositeEntityStore. This is used because the backend uses names as ids.
 */
// eslint-disable array-callback-return
const getChildrenNames = (entityStore: CompositeEntityStore, entityStores: readonly EntityStore[]) =>
    entityStore.children.map(childId => {
        for (const childEntityStore of entityStores) {
            if (childEntityStore.id === childId) {
                return childEntityStore.name;
            }
            if (childEntityStore.typeId === ModelType.Hierarchical) {
                const foundChild = (<HierarchicalEntityStore>childEntityStore).children.find(child => child.id === childId);
                if (foundChild) {
                    return `${childEntityStore.name}::${foundChild.name}`;
                }
            } else {
                const foundRoleChild = childEntityStore.entityRoleStoreHouse.findById(childId);
                if (foundRoleChild) {
                    return `${childEntityStore.name}:${foundRoleChild.name}`;
                }
            }
        }
    });

/*
 * Loads an app version.
 */
const loadAppVersion = (rootStore: RootStore) => async ({ context, newAppVersionId }: HandlerParams<{ newAppVersionId: string }>) => {
    const { appId, versionId } = context;
    const appVersionService = getAppVersionService({ context });
    const newAppVersion = await appVersionService.getAppVersionById({ id: newAppVersionId });
    /**
     * Because the backend activates the version when we migrate as
     * it creates a new version and whenever the backend create a new
     * version it activates it so we have to activate the current version again
     */
    await appVersionService.activateAppVersion({ id: versionId });
    const appStore = rootStore.luisAppStoreHouse.findById(appId);
    appStore.appVersionStoreHouse.add(newAppVersion);
};

/**
 * Represents a handler group for composite entity operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createCompositeEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const { addOrReplaceEntityStore, getAppVersionStore, getEntityStore } = getEntityHandlerGroupUtils(rootStore);
    const logger = getLogger();

    /**
     * Loads a given composite entity.
     *
     * @param id The id of the composite entity to load.
     */
    const loadCompositeEntity = async ({ id, context }: HandlerParams<Pick<CompositeEntityStore, 'id'>>) => {
        const { appId, versionId } = context;
        const compositeEntityService = getCompositeEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<CompositeEntityStore>('LoadCompositeEntity', () => ({ entityId: id }));
        const compositeEntityStore = await logTimedActivity(compositeEntityService.getEntity({ id }));
        addOrReplaceEntityStore({ appId, versionId }, compositeEntityStore);
    };

    /**
     * Creates a new composite entity.
     *
     * @param name The composite entity name.
     * @param children The composite entity children.
     */
    const createCompositeEntity = async ({
        name,
        children,
        context,
        tracker
    }: HandlerParams<Pick<CompositeEntityStore, 'name' | 'children'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const compositeEntityService = getCompositeEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<CompositeEntityStore>('CreateCompositeEntity', (result, error) => {
                const logData = { entityName: name, children };

                return error ? logData : { ...logData, entityId: result.id };
            });
            const entityStore = await logTimedActivity(compositeEntityService.createEntity({ name, children }));
            addGlobalFeaturesToNewModels(entityStore, appId, versionId, rootStore);

            getAppVersionStore({ appId, versionId }).entityStoreHouse.add(entityStore);
            if (tracker) {
                tracker.setContext(entityStore);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes a give composite entity.
     *
     * @param id The id of the composite entity to delete.
     */
    const deleteCompositeEntity = async ({ id, context, tracker }: HandlerParams<Pick<CompositeEntityStore, 'id'>>) =>
        flow(async () => {
            if (tracker) {
                tracker.setContext(id);
            }
            const { appId, versionId } = context;
            const compositeEntityService = getCompositeEntityService({ context });
            const {
                entityStoreHouse: { items: entityStores },
                intentStoreHouse: { items: intentStores }
            } = rootStore.currentLuisAppStore.currentAppVersionStore;
            const logTimedActivity = logger.createTimedActivityLogger('DeleteCompositeEntity', () => ({ entityId: id }));
            await logTimedActivity(compositeEntityService.deleteEntity({ id }));
            // Deletes the dangling pointers for the models that uses these entities as a feature
            deleteDanglingFeaturePointer({ referencedFeatureId: id, intentStores, entityStores });
            getAppVersionStore({ appId, versionId }).entityStoreHouse.removeById(id);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Renames a given entity.
     *
     * @param id The id of the entity to update.
     * @param name Optional: the composite entity new name.
     * @param children Optional: the composite entity new children.
     */
    const updateCompositeEntity = async ({
        id,
        name,
        children,
        context
    }: HandlerParams<Partial<Pick<CompositeEntity, 'name' | 'children'>> & Pick<CompositeEntityStore, 'id'>>) =>
        flow(async () => {
            const {
                currentLuisAppStore: {
                    currentAppVersionStore: { entityStoreHouse }
                }
            } = rootStore;
            const { appId, versionId } = context;
            const compositeEntityService = getCompositeEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('UpdateCompositeEntity', () => ({ entityName: name, entityId: id }));
            const entityStore = <CompositeEntityStore>getEntityStore({ appId, versionId, entityId: id });
            const entityChildrenNames = children?.map(c => c.name) || getChildrenNames(entityStore, entityStoreHouse.items);
            await logTimedActivity(compositeEntityService.updateEntity({ id, name, childrenNames: entityChildrenNames }));
            entityStore.rename(name);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Creates a new child entity for a given composite entity.
     *
     * @param id The composite entity id.
     * @param childName The new child entity name.
     */
    const createChildEntityForCompositeEntity = async ({
        id,
        childName,
        context
    }: HandlerParams<Pick<CompositeEntityStore, 'id'> & { childName: string }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const compositeEntityService = getCompositeEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<LuisId>('CreateChildEntityForCompositeEntity', () => ({
                entityName: name,
                entityId: id,
                childName
            }));
            const childId = await logTimedActivity(compositeEntityService.createChildEntity({ id, childName }));
            getEntityStore<CompositeEntityStore>({ appId, versionId, entityId: id }).addChild(childId);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes children entity from a given composite entity.
     *
     * @param id The composite entity id.
     * @param childIds The ids of the children to delete.
     */
    const deleteChildrenEntityForCompositeEntity = async ({
        id: entityId,
        childIds,
        context
    }: HandlerParams<Pick<CompositeEntityStore, 'id'> & { childIds: LuisId[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const compositeEntityService = getCompositeEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<void[]>('DeleteChildrenEntityForCompositeEntity', () => ({
                entityId
            }));
            const servicePromises = childIds.map(childId => compositeEntityService.deleteChildEntity({ id: entityId, childId }));
            await logTimedActivity(Promise.all(servicePromises));
            childIds.forEach(id => getEntityStore<CompositeEntityStore>({ appId, versionId, entityId }).removeChildById(id));
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Dismisses the migration process by removing the notification of the migration
     * and setting up the date of the dismissal to remind the user with the migration
     * after 1 week.
     */
    const dismissMigration = async () => {
        getStorage('localStorage').setData(LuisConstants.StorageKeys.LastDismissalMigrationDateStorageKey, new Date().toString());
        rootStore.toastStoreHouse.removeById(LuisConstants.MigrationNotificationToastId);
    };

    /**
     * Migrates the given composite entities.
     */
    const migrateCompositeEntities = async ({
        context,
        compositesNames,
        migrationTracker,
        upgradedVersionId
    }: HandlerParams<{
        migrationTracker: CompositeEntitiesMigrationTrackerStore;
        upgradedVersionId: string;
        compositesNames: string[];
    }>) => {
        const migrationSwagger = await getCompositeEntityService({ context }).migrate({
            compositesNames,
            versionId: upgradedVersionId,
            handler: {
                retryStrategy: OperationRetryStrategies.defaultRetryStrategy,
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                handleCall: data => {
                    switch (data.status) {
                        case 'running':
                            migrationTracker.setState('creatingNewVersion');
                            migrationTracker.setStateProgressPercentage(data.progressPercent);
                            break;
                        case 'failed':
                            migrationTracker.setState('migrationFailed');
                            migrationTracker.setFailureReason(data.errorDetails);
                            break;
                        default:
                    }
                }
            }
        });
        if (migrationSwagger) {
            migrationTracker.setUpdatedEntitiesIds(migrationSwagger.updatedEntities);
            // Loads the upgraded version from the backend.
            await loadAppVersion(rootStore)({ context, newAppVersionId: upgradedVersionId });
            migrationTracker.setState('trainingNewVersion');
            // Train the upgraded version
            await trainAppVersion(rootStore)({ context: { ...context, versionId: upgradedVersionId } });
            // Save that the migration is successfully done and the ids of the newly upgraded entities.
            getStorage('sessionStorage').setData(
                LuisConstants.StorageKeys.MigratedEntitiesIdsStorageKey,
                JSON.stringify(migrationSwagger.updatedEntities)
            );
            await dismissMigration();
        }
    };

    return {
        dismissMigration,
        loadCompositeEntity,
        createCompositeEntity,
        updateCompositeEntity,
        deleteCompositeEntity,
        migrateCompositeEntities,
        createChildEntityForCompositeEntity,
        deleteChildrenEntityForCompositeEntity
    };
};
